import { call, fork, put, select, takeEvery, take, takeLatest, takeLeading } from 'redux-saga/effects';
import { isEmpty, omit } from 'lodash';
import * as Types from 'store/state/uploadBulletin/types';
import {
  editListingAction,
  editListingSilentAction,
  NewListingParams,
  newListingAction,
  reportAddressErrorAction,
  editListingStatusAction,
  editListingPromoteStatusAction,
  validateUploadPhoneAction,
  listingsTableInlineUpdateSuccess,
  editListingAssignedAgentAction,
} from 'store/state/uploadBulletin/actions';
import {
  mutate,
  SetMutationResponseAction,
  resetMutationResponse,
} from 'store/state/mutationsResponse/actions';
import {
  IConsentType,
  IEditCommercialListingResponse,
  IEditListingResponse,
  INewCommercialListingResponse,
  INewListingResponse,
  IUpdateUserProfileVariables,
  ListingResponseType,
  LoadType,
  MutationType,
  UpdateListingResponse,
  ListingValidationResult,
  PhoneValidationStatus,
} from './apiService/types';
import { RootAction } from 'store/state';
import { SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { Route } from 'config/routes';
import { navigateTo } from 'store/state/router/actions';
import { uploadBulletinDraft, wizardCreatedListing } from 'components/bulletin-form/wizard/helpers';
import {
  closeManageListingsErrorModal,
  setAfterUploadBulletinModalOpen,
  setIsListingsTableLoading,
  setListingEdited,
  setManageListingsErrorModal,
  upgradeUserPlanFlow,
  setPrivateUserMonetizationEventsSource,
  setToastMessage,
  setPrivateBulletinMinimumPromotionPlanModal,
  UpgradeUserPlanFlowPayload,
  setAfterListingDeleteModalOpen,
} from 'store/state/app/actions';
import { AfterUploadBulletinModalPoi } from 'store/state/app';
import {
  BulletinStatusType,
  IEditBulletinInput,
  INewBulletinCommercialInput,
  INewBulletinInput,
  INewBulletinResidentialInput,
  PocType,
  PoiId,
  User,
  ListingStatusType,
  IGeneralCondition,
} from 'utils/entities';
import { isAgentConsoleUserSelector, isAgentManagerSelector, isAgentSelector, isPrivateUserSelector, userProfileSelector } from 'store/state/domainData/selectors';
import { resetDomainData } from 'store/state/domainData/actions';
import { CLOSE_MANAGE_LISTINGS_ERROR_MODAL, SET_AFTER_UPLOAD_BULLETIN_MODAL_OPEN } from 'store/state/app/types';
import { routeSelector, routeNameSelector } from 'store/state/selectors/router';
import { manageListingsErrorModalSelector } from 'store/state/app/selectors';
import { allPoisByIdsHeapSelector } from 'store/state/selectors/poisHeap';
import { queryData } from 'store/sagas/apiService';
import { isPhoneRegisteredDomainSelector } from 'store/state/domainData/selectors/common';
import { PrivateUserMonetizationSource } from 'store/state/app';
import { newListingValidationResponseSelector, IMutationResponse, newListingResponseSelector, validationCodeResponseSelector, editListingValidationResponseSelector, editCommercialListingResponseSelector, editListingResponseSelector, newCommercialListingResponseSelector } from 'store/state/mutationsResponse/selectors';
import { makeIsBulletinBasicPromotionSelector } from 'store/state/app/private-user-monetization/selectors';
import { TRANSITION_SUCCESS } from 'store/state/router/types';
import { loadActivePlans, loadCurrentPromotionPlan } from './privateUserMonetizationWatcher';
import { SET_PRIVATE_BULLETIN_MINIMUM_PLAN_MODAL } from 'store/state/app/private-user-monetization/types';

function isSuccessfulValidation(result: ListingValidationResult): boolean {
  return Boolean(
    result
    && result.__typename === ListingResponseType.SuccessfulListingValidation
    && result.valid
  );
}

const REMARKS_DELIMITER = '___';

const newListingMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.NewListing
);

const afterUploadPattern = (action: RootAction) => (
  (action.type === TRANSITION_SUCCESS
  && (action.payload.route.name === Route.UnitPage || action.payload.route.name === Route.UnitPageCommercial || action.payload.route.name === Route.ManageBulletins)
  && (action.payload.previousRoute && action.payload.previousRoute.name === Route.UploadBulletin))
  || (action.type === TRANSITION_SUCCESS && action.payload.route.name === Route.ManageBulletins && action.payload.route.params && Boolean(action.payload.route.params.payment_outcome))
);

const newListingValidationMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.NewListingValidation
);

const editListingValidationMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.EditListingValidation
);

const newCommercialListingMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.NewCommercialListing
);

const editListingMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.EditListing
);

const editCommercialListingMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.EditCommercialListing
);

const listingInlineUpdatePattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && (
    action.mutationType === MutationType.EditListingStatus ||
    action.mutationType === MutationType.EditCommercialListingStatus ||
    action.mutationType === MutationType.EditListingPromotionStatus ||
    action.mutationType === MutationType.EditCommercialListingPromotionStatus ||
    action.mutationType === MutationType.EditCommercialListingAssignedAgent ||
    action.mutationType === MutationType.EditListingAssignedAgent
  )
);

const reportAddressSuccessPattern = (action: RootAction) => action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.UploadBulletinBadAddressReport;

function* reportErrorAddressWatcher(action: ReturnType<typeof reportAddressErrorAction>) {
  const {  email, message, docId, id, reportSource, dealType } = action.payload;
  const remarks = message + REMARKS_DELIMITER + email;

  yield put(mutate({
    mutationType: MutationType.UploadBulletinBadAddressReport,
    meta: {
      variables: {
        docId,
        remarks,
        extraData: {
          reportSource,
          dealType,
          id,
          message,
          email,
        },
      },
    },
  }));
}

function* reportErrorAddressSuccessWatcher(action: SetMutationResponseAction<MutationType.UploadBulletinBadAddressReport>) {
  const response = action.meta.response;

  if (response && response.data && response.data.badAddressReport) {
    yield put(setToastMessage({ term: 'reportListing.popup.toasterMessageConfirmation' }));
  }
}

function* consentAcceptanceWorker() {
  yield put(mutate({
    mutationType: MutationType.AcceptConsents,
    meta: {
      variables: {
        input: {
          consents: [ IConsentType.LISTING_UPLOAD_TERMS_AND_CONDITIONS ],
        },
      },
    },
  }));
}

const generalConditionNewMap = {
  [IGeneralCondition.ToRenovated]: IGeneralCondition.ToRenovate,
};

function mapGeneralConditionToNew(generalCondition: IGeneralCondition): IGeneralCondition {
  return generalConditionNewMap[generalCondition] || generalCondition;
}

export function* prepareListingData (listing: INewBulletinInput | IEditBulletinInput, isEdit?: boolean, withConsent: boolean = true) {
  const isAgent = yield select(isAgentSelector);
  const isAgentManager = yield select(isAgentManagerSelector);
  const { type, userName, userPhone, generalCondition, ...rest } = listing;
  const assignedAgentUserId = 'assignedAgentUserId' in listing && isAgentManager ? listing.assignedAgentUserId : undefined;

  if (withConsent) {
    yield fork(consentAcceptanceWorker);
  }

  return  {
    ...omit(rest, 'sellerType'),
    ...(isEdit ? {} : { sellerType: isAgent || isAgentManager ? PocType.Agent : PocType.Private }),
    generalCondition: mapGeneralConditionToNew(generalCondition),
    userName: isAgent ? null : userName,
    userPhone: isAgent ? null : userPhone,
    assignedAgentUserId,
  };
}

function* editListingWatcher(action: ReturnType<typeof editListingAction>) {
  const { payload: { listing, isUploadFlow } } = action;
  const data = yield call(prepareListingData, listing, true);

  if (listing.type === 'commercialBulletin') {
    yield put(mutate({
      mutationType: MutationType.EditCommercialListing,
      meta: {
        variables: { editCommercialListing: data },
      },
    }));
    yield take(editCommercialListingMutationPattern);
    const res: IMutationResponse<MutationType.EditCommercialListing> = yield select(editCommercialListingResponseSelector);

    yield call(editCommercialListingResponseWatcher, res.data, isUploadFlow);
  }
  else {
    yield put(mutate({
      mutationType: MutationType.EditListing,
      meta: {
        variables: { editListing: data },
      },
    }));
    yield take(editListingMutationPattern);
    const res: IMutationResponse<MutationType.EditListing> = yield select(editListingResponseSelector);

    yield call(editResidentialListingResponseWatcher, res.data, isUploadFlow);
  }
}

function* editListingSilentWorker(action: ReturnType<typeof editListingSilentAction>) {
  const { payload: { listing } } = action;
  const data = yield call(prepareListingData, listing, true, false);
  yield put(mutate({
    mutationType: MutationType.EditListing,
    meta: {
      variables: { editListing: data },
    },
  }));
}

function* createNewListingWatcher(action: ReturnType<typeof newListingAction>) {
  const { payload: { listing, source } } = action;
  const skipConsent = listing.type === 'bulletin';
  const data = yield call(prepareListingData, listing, false, skipConsent);

  if (listing.type === 'commercialBulletin') {
    yield put(mutate({
      mutationType: MutationType.NewCommercialListing,
      meta: {
        variables: { newCommercialListing: data as INewBulletinCommercialInput },
      },
    }));
    yield take(newCommercialListingMutationPattern);
    const res: IMutationResponse<MutationType.NewCommercialListing> = yield select(newCommercialListingResponseSelector);

    yield call(newCommercialListingResponseWatcher, res.data);
  }
  else {
    yield put(mutate({
      mutationType: MutationType.NewListing,
      meta: {
        variables: { newListing: data as INewBulletinResidentialInput },
      },
    }));
    yield take(newListingMutationPattern);
    const res: IMutationResponse<MutationType.NewListing> = yield select(newListingResponseSelector);

    yield call(newResidentialListingResponseWorker, res.data, source);
  }

  const isAgent = yield select(isAgentSelector);
  if (isAgent) return;

  const currentUser: User = yield select(userProfileSelector);
  const metaVariables: IUpdateUserProfileVariables = {};
  if (!currentUser.firstName) {
    const [ firstName, lastName ] = data.userName.split(' ');
    if (firstName) {
      metaVariables.firstName = firstName;
    }
    if (lastName) {
      metaVariables.lastName = lastName;
    }
  }
  if (!currentUser.phone) {
    metaVariables.phone = data.userPhone;
  }
  if (!isEmpty(metaVariables)) {
    yield put(mutate({
      mutationType: MutationType.UpdateUserProfile,
      meta: {
        variables: metaVariables,
      },
    }));
  }
}

function* listingResponseErrorWatcher(data: ListingValidationResult | INewListingResponse | IEditListingResponse | INewCommercialListingResponse | IEditCommercialListingResponse) {
  if (!data) {
    yield put(setToastMessage({ term: 'bulletinForm.wizard.errorToast', type: 'error' }));
    return;
  }

  if (data.__typename === ListingResponseType.UserCannotAddListingError) {
    yield put(setPrivateUserMonetizationEventsSource(PrivateUserMonetizationSource.WizardUpload));
  }

  switch (data.__typename) {
    case ListingResponseType.UserCannotAddListingError:
    case ListingResponseType.GeneralListingError:
    case ListingResponseType.InvalidInputError:
      yield put(setManageListingsErrorModal({ type: data.__typename, field: data.field }));
      break;
    case ListingResponseType.NoPermissionsError:
      yield put(setToastMessage({ term: 'bulletinForm.wizard.errorToast', type: 'error' }));
      break;
    case ListingResponseType.SuccessfulListingValidation:
      break;
    default: {
      yield fork(navigateToListingsPageWorker);
    }
  }
}

function* offerTrialPromotionPlanFlow(bulletinId: PoiId, poi: AfterUploadBulletinModalPoi, source: PrivateUserMonetizationSource) {
  yield put(setAfterUploadBulletinModalOpen({ uploadType: 'UPLOAD', poi }));
  yield take((action: RootAction) => action.type === SET_AFTER_UPLOAD_BULLETIN_MODAL_OPEN && action.payload === null);
  yield call(loadActivePlans, bulletinId);
  yield put(setPrivateBulletinMinimumPromotionPlanModal({ isOpen: true, bulletin: { id: bulletinId } }));
  yield put(setPrivateUserMonetizationEventsSource(source));
  yield take((action: RootAction) => action.type === SET_PRIVATE_BULLETIN_MINIMUM_PLAN_MODAL && action.payload && action.payload.isOpen === false);
  yield put(setPrivateUserMonetizationEventsSource(null));
}

function* newListingResponseHandler(input: {
  bulletinId: PoiId;
  isCommercial: boolean;
  source: NewListingParams['source'] | null;
}) {
  const { bulletinId, isCommercial, source } = input;

  if (source === 'wizardPaidPlanViaHostedFields') {
    wizardCreatedListing.set({ id: bulletinId, type: isCommercial ? 'commercialBulletin' : 'bulletin' });
    yield put(resetDomainData({ loadType: LoadType.UserAllListings }));
    return;
  }

  yield put(navigateTo(isCommercial ? Route.UnitPageCommercial : Route.UnitPage, { id: bulletinId }, { replace: true }));

  yield call(loadCurrentPromotionPlan);
  const isBasicPlan = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));
  yield put(setAfterUploadBulletinModalOpen(null));

  const afterUploadPoi: AfterUploadBulletinModalPoi = {
    id: bulletinId,
    type: isCommercial ? 'commercialBulletin' : 'bulletin',
  };

  if (isBasicPlan && source !== 'wizardGrantPlan') {
    yield call(offerTrialPromotionPlanFlow, bulletinId, afterUploadPoi, PrivateUserMonetizationSource.ListingPage);
  }
  else {
    yield put(setAfterUploadBulletinModalOpen({ uploadType: 'UPLOAD', poi: afterUploadPoi }));
  }
  yield put(resetDomainData({ loadType: LoadType.UserAllListings }));
  yield put(resetMutationResponse({ mutationType: MutationType.NewListing, key: null }));
  uploadBulletinDraft.clear();
}

function* newCommercialListingResponseWatcher(data: { newCommercialListing: INewCommercialListingResponse; }) {
  const newCommercialListing = data && data.newCommercialListing;
  const commercialListing = newCommercialListing && newCommercialListing.commercialListing;

  if (commercialListing) {
    yield call(newListingResponseHandler, { bulletinId: commercialListing.id, isCommercial: true, source: null });
  }
  else {
    yield fork(listingResponseErrorWatcher, newCommercialListing);
  }
}

function* newResidentialListingResponseWorker(
  data: { newListing: INewListingResponse },
  source: NewListingParams['source']
) {
  if (data && data.newListing) {
    const { listing } = data.newListing;

    if (listing) {
      if (source === 'wizardPaidPlanViaIframe') {
        yield put(resetMutationResponse({ mutationType: MutationType.NewListing, key: null }));
        uploadBulletinDraft.clear();
        return;
      }
      yield call(newListingResponseHandler, { bulletinId: data.newListing.listing.id, isCommercial: false, source });
    }
    else {
      yield fork(listingResponseErrorWatcher, data.newListing);
    }
  }
  else {
    yield fork(listingResponseErrorWatcher, undefined);
  }
}

export function* navigateToListingsPageWorker() {
  const isAgentConsoleUser: boolean = yield select(isAgentConsoleUserSelector);
  yield put(navigateTo(isAgentConsoleUser ? Route.Listings : Route.ManageBulletins, {}, { replace: true }));
}

function* editListingResponseWorker(listing: UpdateListingResponse, isCommercial?: boolean, isUploadFlow?: boolean) {
  const { id, status: { status } } = listing;
  yield put(resetDomainData({ loadType: LoadType.UnitPageBulletin }));
  yield put(resetDomainData({ loadType: LoadType.UserAllListings }));
  yield put(setListingEdited(Date.now()));
  if (status === BulletinStatusType.Displayed) {
    yield put(navigateTo(isCommercial ? Route.UnitPageCommercial : Route.UnitPage, { id }, { replace: true, includeFrozen: true }));

    const afterUploadPoi: AfterUploadBulletinModalPoi = {
      id,
      type: isCommercial ? 'commercialBulletin' : 'bulletin',
    };

    if (isUploadFlow) {
      yield call(loadCurrentPromotionPlan);
      const isBasicPlan = yield select(makeIsBulletinBasicPromotionSelector(id));
      yield put(setAfterUploadBulletinModalOpen(null));

      if (isBasicPlan) {
        yield call(offerTrialPromotionPlanFlow, id, afterUploadPoi, PrivateUserMonetizationSource.ListingPage);
      }
      else {
        yield put(setAfterUploadBulletinModalOpen({ uploadType: 'UPLOAD', poi: afterUploadPoi }));
      }
    }
    else {
      yield put(setAfterUploadBulletinModalOpen({ uploadType: 'UPDATE', poi: afterUploadPoi }));
    }
  }
  else {
    yield fork(navigateToListingsPageWorker);
  }
  yield put(resetDomainData({ loadType: LoadType.EditListingPoi }));
}

function* editResidentialListingResponseWatcher(data: { editListing: INewListingResponse; }, isUploadFlow?: boolean) {
  if (data && data.editListing && data.editListing.listing) {
    yield call(editListingResponseWorker, data.editListing.listing, false, isUploadFlow);
  }
  else {
    yield fork(listingResponseErrorWatcher, data ? data.editListing : undefined);
  }
}

function* editCommercialListingResponseWatcher(data: { editCommercialListing: INewCommercialListingResponse; }, isUploadFlow?: boolean) {
  if (data && data.editCommercialListing && data.editCommercialListing.commercialListing) {
    yield call(editListingResponseWorker, data.editCommercialListing.commercialListing, true, isUploadFlow);
  }
  else {
    yield fork(listingResponseErrorWatcher, data ? data.editCommercialListing : undefined);
  }
}

type InlineUpdateMutationAction = SetMutationResponseAction<
    MutationType.EditCommercialListingStatus
  | MutationType.EditCommercialListingPromotionStatus
  | MutationType.EditListingStatus
  | MutationType.EditListingPromotionStatus
  | MutationType.EditCommercialListingAssignedAgent
  | MutationType.EditListingAssignedAgent
>;

function* listingInlineUpdateResponseWatcher(action: InlineUpdateMutationAction) {
  const { data } = action.meta.response;
  let isSuccess = false;
  let upgradeUserPlanFlowPayload: UpgradeUserPlanFlowPayload | null = null;

  const routeName = yield select(routeNameSelector);
  const isManageListings = routeName === Route.ManageBulletins;

  const successToastType = isManageListings ? 'success-dark' : 'success';

  switch (action.mutationType) {
    case MutationType.EditCommercialListingStatus:
      if ('editCommercialListingStatus' in data && data.editCommercialListingStatus.commercialListing) {
        isSuccess = true;
        yield put(setToastMessage({ term: 'listingsPage.changeStatusModal.toast.success', params: { newStatus: data.editCommercialListingStatus.commercialListing.status.status }, type: successToastType }));
      }
      break;
    case MutationType.EditListingStatus:
      if ('editListingStatus' in data && data.editListingStatus.listing) {
        isSuccess = true;
        const newStatus = data.editListingStatus.listing.status.status;
        const bulletinId = data.editListingStatus.listing.id;

        if (newStatus === BulletinStatusType.Deleted && 'editListingStatus' in action.meta.variables && !action.meta.variables.isSubscription) {
          yield put(setAfterListingDeleteModalOpen(true));
        }
        else {
          yield put(setToastMessage({ term: 'listingsPage.changeStatusModal.toast.success', params: { newStatus }, type: successToastType }));
        }

        if (newStatus === BulletinStatusType.Displayed && bulletinId) {
          const isPrivateUser = yield select(isPrivateUserSelector);
          const poisHeap = yield select(allPoisByIdsHeapSelector);
          const poi = poisHeap && poisHeap[bulletinId];
          const isPrivateResidentialBulletin = isPrivateUser && (poi && poi.type === 'bulletin');

          if (isPrivateResidentialBulletin) {
            const isBasicPlan = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));
            if (isBasicPlan) {
              upgradeUserPlanFlowPayload = {
                bulletinId,
                userInitiated: false,
                source: PrivateUserMonetizationSource.ManageBulletins,
              };
            }
          }
        }
      }
      break;
    case MutationType.EditCommercialListingPromotionStatus:
      if ('editCommercialListingPromotionStatus' in data && data.editCommercialListingPromotionStatus.commercialListing) {
        isSuccess = true;
        yield put(setToastMessage({ term: 'listingsPage.changePromotionStatus.toast.success', params: { newPromotionStatus: data.editCommercialListingPromotionStatus.commercialListing.status.promoted }, type: successToastType }));
      }
      break;
    case MutationType.EditListingPromotionStatus:
      if ('editListingPromotionStatus' in data && data.editListingPromotionStatus.id) {
        isSuccess = true;
        yield put(setToastMessage({ term: 'listingsPage.changePromotionStatus.toast.success', params: { newPromotionStatus: data.editListingPromotionStatus.promoted }, type: successToastType }));
      }
      break;
    case MutationType.EditCommercialListingAssignedAgent:
      if ('editAssignedAgentCommercialListing' in data && data.editAssignedAgentCommercialListing.commercialListing) {
        isSuccess = true;
        yield put(setToastMessage({ term: 'listingsPage.changeAssignedAgent.success', type: 'success' }));
      }
      break;
    case MutationType.EditListingAssignedAgent:
      if ('editAssignedAgentListing' in data && data.editAssignedAgentListing.listing) {
        isSuccess = true;
        yield put(setToastMessage({ term: 'listingsPage.changeAssignedAgent.success', type: 'success' }));
      }
      break;
  }

  if (isSuccess) {
    yield put(listingsTableInlineUpdateSuccess());
    if (upgradeUserPlanFlowPayload) {
      yield put(upgradeUserPlanFlow(upgradeUserPlanFlowPayload));
    }
  }
  else {
    yield fork(listingResponseErrorWatcher, undefined);
    yield put(setIsListingsTableLoading(false));
  }
}

function* closeErrorModalWatcher(action: ReturnType<typeof closeManageListingsErrorModal>) {
  const route = yield select(routeSelector);

  if (route.name === Route.UploadBulletin) {
    const modalData = yield select(manageListingsErrorModalSelector);
    if (modalData && modalData.type === ListingResponseType.UserCannotAddListingError) {
      uploadBulletinDraft.clear();
      wizardCreatedListing.clear();
    }
  }

  if (route.name !== Route.ManageBulletins || route.name !== Route.Listings) {
    yield put(resetDomainData({ loadType: LoadType.UserAllListings }));
    if (!action.payload || !action.payload.noRedirect) {
      yield fork(navigateToListingsPageWorker);
    }
  }

  yield put(setManageListingsErrorModal(null));
}


const PoiStatusToListingStatusMap: Record<BulletinStatusType, ListingStatusType> = {
  [BulletinStatusType.Displayed]: ListingStatusType.Displayed,
  [BulletinStatusType.Deleted]: ListingStatusType.Deleted,
  [BulletinStatusType.Frozen]: ListingStatusType.Frozen,
  [BulletinStatusType.CancelSubscription]: ListingStatusType.Frozen,
};

function* editListingStatusWorker(action: ReturnType<typeof editListingStatusAction>) {
  const { payload: { id, type, status, hideReason, source, isSubscription } } = action;
  const isBulletin = type === 'bulletin';
  const mutationType = isBulletin ? MutationType.EditListingStatus : MutationType.EditCommercialListingStatus;

  yield put(setIsListingsTableLoading(true));

  yield put(mutate({
    mutationType,
    meta: {
      variables: {
        editListingStatus: {
          status: PoiStatusToListingStatusMap[status],
          id,
          hideReason,
        },
        source,
        isSubscription,
      },
    },
  }));
}

function* editAssignedAgentWorker(action: ReturnType<typeof editListingAssignedAgentAction>) {
  const { payload: { id, type, agentId, userId } } = action;
  const isBulletin = type === 'bulletin';

  yield put(setIsListingsTableLoading(true));

  if (isBulletin) {
    yield put(mutate({
      mutationType: MutationType.EditListingAssignedAgent,
      meta: {
        variables: {
          editAssignedAgentListing: {
            listingId: id,
            assignedAgentId: agentId,
          },
        },
      },
    }));
  }
  else {
    yield put(mutate({
      mutationType: MutationType.EditCommercialListingAssignedAgent,
      meta: {
        variables: {
          editAssignedAgentCommercialListing: {
            listingId: id,
            assignedAgentUserId: userId,
          },
        },
      },
    }));
  }
}

function* editListingPromoteStatusWorker(action: ReturnType<typeof editListingPromoteStatusAction>) {
  const { payload: { id, promotedStatus: promoted, type } } = action;
  const isBulletin = type === 'bulletin';

  yield put(setIsListingsTableLoading(true));

  if (isBulletin) {
    yield put(mutate({
      mutationType: MutationType.EditListingPromotionStatus,
      meta: {
        variables: {
          editListingPromotionStatus: {
            id,
            promoted,
          },
        },
      },
    }));
  }
  else {
    yield put(mutate({
      mutationType: MutationType.EditCommercialListingPromotionStatus,
      meta: {
        variables: {
          editCommercialListingPromotionStatus: {
            id,
            promoted,
          },
        },
      },
    }));
  }
}

function* afterUploadWorker() {
  uploadBulletinDraft.clear();
  wizardCreatedListing.clear();
}

function* validatePhoneWorker(action: ReturnType<typeof validateUploadPhoneAction>) {
  yield put(resetMutationResponse({ mutationType: MutationType.GenerateValidationCode, key: null }));
  yield put(resetMutationResponse({ mutationType: MutationType.CheckValidationCode, key: null }));
  yield put(resetMutationResponse({ mutationType: MutationType.NewListingValidation, key: null }));
  yield put(resetMutationResponse({ mutationType: MutationType.EditListingValidation, key: null }));
  yield put(resetDomainData({ loadType: LoadType.IsPhoneRegistered }));

  const { phone: phoneNumber } = action.payload;

  yield call(queryData, {
    loadType: LoadType.IsPhoneRegistered,
    meta: { variables: { phoneNumber } },
  });

  const isRegisteredResponse = yield select(isPhoneRegisteredDomainSelector);
  const isRegistered = isRegisteredResponse && isRegisteredResponse.data ? isRegisteredResponse.data.isPhoneRegistered : null;

  if (isRegistered === false) {
    yield put(mutate({
      mutationType: MutationType.GenerateValidationCode,
      meta: { variables: { phoneNumber } },
    }));
    let validateCodeStatus: boolean = null;
    while (!validateCodeStatus) {
      yield take((act: RootAction) => act.type === SET_MUTATION_RESPONSE && act.mutationType === MutationType.CheckValidationCode);
      const checkCodeResponse = yield select(validationCodeResponseSelector);
      validateCodeStatus = checkCodeResponse && checkCodeResponse.data
        && checkCodeResponse.data.checkValidationCode && checkCodeResponse.meta.variables.phoneNumber === phoneNumber
        ? checkCodeResponse.data.checkValidationCode === PhoneValidationStatus.Success : null ;
    }
  }
  const { isEdit, listing, isBasicPlan } = action.payload;
  const storedListing = wizardCreatedListing.get();
  if (listing && listing.type === 'bulletin') {
    if (isEdit || storedListing) {
      const listingId = storedListing ? storedListing.id : (listing as IEditBulletinInput).id;
      const listingData = yield call(prepareListingData, { ...listing, id: listingId }, true, false);
      yield put(mutate({
        mutationType: MutationType.EditListingValidation,
        meta: {
          variables: { editListing: listingData },
        },
      }));
      yield take(editListingValidationMutationPattern);

      const data: IMutationResponse<MutationType.EditListingValidation> = yield select(editListingValidationResponseSelector);
      if (data && data.data) {
        yield fork(listingResponseErrorWatcher, data.data.editListingValidation);
      }

      if (isEdit && data && data.data && isSuccessfulValidation(data.data.editListingValidation)) {
        yield put(editListingAction({ listing: listingData }));
      }
    }
    else {
      const listingData = yield call(prepareListingData, listing, false, false);

      yield put(mutate({
        mutationType: MutationType.NewListingValidation,
        meta: { variables: { newListing: listingData as INewBulletinResidentialInput } },
      }));
      yield take(newListingValidationMutationPattern);

      const data: IMutationResponse<MutationType.NewListingValidation> = yield select(newListingValidationResponseSelector);
      if (data && data.data) {
        yield fork(listingResponseErrorWatcher, data.data.newListingValidation);
      }
      if (!isBasicPlan && data && data.data && isSuccessfulValidation(data.data.newListingValidation)) {
        yield put(newListingAction({ listing: listingData }));
      }
    }
  }
}


export function* addEditBulletinWatcher() {
  yield takeLatest(Types.REPORTING_ERROR_ADDRESS, reportErrorAddressWatcher);
  yield takeLatest(reportAddressSuccessPattern, reportErrorAddressSuccessWatcher);
  yield takeLeading(Types.NEW_LISTING, createNewListingWatcher);
  yield takeLatest(Types.EDIT_LISTING, editListingWatcher);
  yield takeLatest(Types.EDIT_LISTING_SILENT, editListingSilentWorker);

  yield takeEvery(Types.EDIT_LISTING_STATUS, editListingStatusWorker);
  yield takeEvery(Types.EDIT_LISTING_ASSIGNED_AGENT, editAssignedAgentWorker);
  yield takeEvery(Types.EDIT_LISTING_PROMOTE_STATUS, editListingPromoteStatusWorker);

  yield takeLatest(listingInlineUpdatePattern, listingInlineUpdateResponseWatcher);

  yield takeLatest(Types.VALIDATE_PHONE, validatePhoneWorker);
  yield takeEvery(afterUploadPattern, afterUploadWorker);
  yield takeEvery(CLOSE_MANAGE_LISTINGS_ERROR_MODAL, closeErrorModalWatcher);
}
